import { HttpClient } from '@angular/common/http';
import { EventEmitter, Injectable } from '@angular/core';
import { BaseStation } from '@app/interfaces/rm/base-station';
import { Chamber, ChamberPort } from '@app/interfaces/rm/chamber';
import { FieldParams } from '@app/interfaces/rm/customize-resource';
import { Matrix } from '@app/interfaces/rm/matrix';
import { Radio } from '@app/interfaces/rm/radio';
import {
    EditForm,
    ResourceInstances,
    ResourceInstancesField,
    ResourceInstancesFields, ResourcePortAdd, ResourcePortData, ResourcePortEdit, ResourcePortList,
} from '@app/interfaces/rm/resource-instances';
import {
    AddResource,
    CheckPortResource,
    EditResource,
    ExportSearchResource, GNBData,
    ReourcesParams,
    Resource,
    ResourceExport, ResourceExportParams,
    ResourceField,
    ResourcesColumnData,
    ResourcesExportParams,
    ResourcesFieldColumn,
    ResourceType,
    ResourceWithPage,
} from '@app/interfaces/rm/resources';
import { UE } from '@app/interfaces/rm/ue';
import { SelectOption } from '@app/interfaces/sys/dict';
import { STColumn, STData } from '@app/interfaces/sys/st';
import resources from '@app/modules/product/rm/resource/components/other-resources/resources';
import { environment } from '@env/environment';
import { TicketService } from '@services/lm/ticket.service';
import { BaseStationService } from '@services/rm/base-station.service';
import { ChamberHttp } from '@services/rm/chamber.service';
import { MatrixHttp } from '@services/rm/matrix.service';
import { RadioHttp } from '@services/rm/radio.service';
import { ResourceDefinitionsService } from '@services/rm/resource-definitions.service';
import { ResourceInstancesService } from '@services/rm/resource-instances.service';
import { UeService } from '@services/rm/ue.service';
import { SettingsService } from '@services/sys/settings.service';
import dayjs from 'dayjs';
import { NzModalService } from 'ng-zorro-antd/modal';
import { filter, map } from 'rxjs/operators';
import { lastValueFrom } from 'rxjs';

@Injectable({
    providedIn: 'root',
})
export class ResourcesService {
    private moduleApi = environment.api.services.rm;
    private baseUrl = this.moduleApi + '/resources';
    public commonEvent = new EventEmitter();

    constructor(private http: HttpClient,
                private resourceDefinitionsService: ResourceDefinitionsService,
                private chamberService: ChamberHttp,
                private radioService: RadioHttp,
                private ueService: UeService,
                private matrixService: MatrixHttp,
                private resourceInstanceService: ResourceInstancesService,
                private customizeResourceService: ResourceDefinitionsService,
                private settingService: SettingsService,
                private baseStationService: BaseStationService,
                private ticketService: TicketService,
                private modal: NzModalService) {
    }

    async find(params: ReourcesParams): Promise<ResourceWithPage> {
        return lastValueFrom(this.http.get<ResourceWithPage>(`${this.baseUrl}`, {
            params: Object.assign(params),
        }));
    }

    async add(params: AddResource): Promise<void> {
        return lastValueFrom(this.http.post<void>(`${this.baseUrl}`, params));
    }

    async one(option: { id: number, resourceType: string }): Promise<any> {
        return lastValueFrom(this.http.get<any>(`${this.baseUrl}/one`, {
            params: Object.assign(option),
        }));
    }

    async edit(data: EditResource): Promise<void> {
        return lastValueFrom(this.http.put<void>(`${this.baseUrl}`, data));
    }

    async checkPortHasConnected(data: EditResource): Promise<CheckPortResource[]> {
        return lastValueFrom(this.http.post<CheckPortResource[]>(`${this.baseUrl}/check-port`, data));
    }

    async delete(params: { resourceType: string, id: number }): Promise<void> {
        return lastValueFrom(this.http.delete<void>(`${this.baseUrl}`, {
            params: Object.assign(params),
        }));
    }

    async searchResource(params: {
        keywordList: string[]
    }): Promise<ExportSearchResource> {
        return lastValueFrom(this.http.get<ExportSearchResource>(`${this.baseUrl}/search`, {
            params: Object.assign(params),
        }));
    }

    async findAllResourceType(needCustom: boolean = true): Promise<ResourceType[]> {
        const otherResources = resources.data.map(item => {
            return {
                value: item.type,
                label: item.title,
                type: 'other',
                key: item.type,
                category: item.category,
                fields: item.field,
                resourceTypeKey: item.type
            };
        });
        const defaultTypes: ResourceType[] = [
            {
                value: 'Chamber',
                label: 'Chamber',
                key: 'CHAMBER',
                category: 'PHYSICAL',
            },
            {
                value: 'Radio',
                label: 'Radio',
                key: 'RADIO',
                category: 'PHYSICAL',
            },
            {
                value: 'Matrix',
                label: 'Matrix',
                key: 'MATRIX',
                category: 'PHYSICAL',
            },
            {
                value: 'Base_Station',
                label: 'Base Station',
                key: 'BASE_STATION',
                category: 'PHYSICAL',
            },
            {
                value: 'UE',
                label: 'UE',
                key: 'UE',
                category: 'PHYSICAL',
            },
            ...otherResources,
        ];
        let customTypes: SelectOption[] = [];
        if (needCustom) {
            customTypes = await this.resourceDefinitionsService.findAllOptions({
                type: 'MODIFIABLE_TYPE'
            });
        }
        return needCustom ? [
            ...defaultTypes,
            ...customTypes.map((item) => {
                return {
                    value: item.value,
                    label: item.label,
                    type: 'customize',
                    permissions: [],
                    customId: item.value,
                    key: 'RESOURCE_INSTANCE',
                    resourceTypeKey: `RESOURCE_INSTANCE_${item.value}`
                };
            }),
        ] : [...defaultTypes];
    }

    async export(data: ResourceExportParams): Promise<any> {
        return this.http.post<any>(`${this.baseUrl}/export-resource`, data, {
            responseType: 'blob' as 'json',
            observe: 'response',
            headers: {
                resultIsFile: 'true',
            },
        }).subscribe((response) => {
            this.ticketService.downloadFile(response);
        });
    }

    async exportResource(data: ResourcesExportParams): Promise<any> {
        return this.http.post<any>(`${this.baseUrl}/export`, data, {
            responseType: 'blob' as 'json',
            observe: 'response',
            headers: {
                resultIsFile: 'true',
            },
        }).subscribe((response) => {
            this.ticketService.downloadFile(response);
        });
    }

    async findResourceListByType(key: string, value?: string | number, keyword?: string, params?: {
        labelIdIn?: number[]
    }): Promise<any[]> {
        let data: any[] = [];
        const query: {
            page: number;
            size: number;
            keyword?: string;
            searchText?: string;
            name?: string;
            sort?: string;
            labelIn?: number[];
        } = {
            ...(params || {}),
            page: 0,
            size: 999999,
            sort: 'name,asc',
        };
        if (keyword) {
            query.keyword = keyword;
        }
        if (key === 'CHAMBER') {
            data = (await this.chamberService.chamberList(query)).content;
        } else if (key === 'RADIO') {
            delete query.keyword;
            if (keyword) {
                query.searchText = keyword;
            }
            data = (await this.radioService.radioList(query)).content;
        } else if (key === 'BASE_STATION') {
            data = (await this.baseStationService.find(query)).content;
        } else if (key === 'UE') {
            data = (await this.ueService.find(query)).content;
        } else if (key === 'MATRIX') {
            data = (await this.matrixService.matrixList(query)).content;
        } else if (key && key.indexOf('RESOURCE_INSTANCE') !== -1) {
            delete query.keyword;
            if (keyword) {
                query.name = keyword;
            }
            data = (await this.resourceInstanceService.find({
                ...query,
                resourceDefinitionId: value as number,
            })).content;
        } else {
            data = (await this.find({
                ...query,
                resourceType: key as string,
            })).content;
        }
        return data;
    }

    getResourceFieldsData(resourceInstance: ResourceInstances): {
        [key: string]: string | number
    } {
        const fields = resourceInstance?.fields;
        let data = {};
        if (fields && fields.length > 0) {
            fields?.forEach(item => {
                data = {
                    ...data,
                    [item.resourceDefinitionFieldTitle]: item.value,
                };
            });
        }
        return {
            ...data,
            numberOfPorts: resourceInstance?.port as number
        };
    }

    getResourceFieldsDetail(resourceInstance: ResourceInstances): {
        label: string;
        value?: string | number;
    }[] {
        const fields = resourceInstance?.fields || [];
        return fields.map(item => {
            return {
                value: item.value,
                label: item.resourceDefinitionFieldTitle,
            };
        });
    }

    async getCustomField(resourceType: string): Promise<FieldParams[]> {
        const params = {
            page: 0,
            size: 50,
            type: 'IMMUTABLE_TYPE',
            title: resourceType,
        };
        const { content } = await this.customizeResourceService.find(params);
        const fieldsArray = content && content.length > 0 ? content[0]?.fields || [] : [];
        const fields = fieldsArray.filter(item => item.displayResource);
        return fields;
    }

    getFieldsSubmitParams(fields: FieldParams[], formData: { [key: string]: string | number }): ResourceInstancesFields[] {
        const fieldsParams = (fields || []).map((item) => {
            return {
                value: formData[item.title],
                resourceDefinitionFieldId: item.id,
            } as ResourceInstancesFields;
        });
        return fieldsParams;
    }

    getResourceColumns(fields: FieldParams[], columns: STColumn[], insertLocation: number): STColumn[] {
        const displayColumns = fields?.filter(item => item.displayResource);
        if (displayColumns && displayColumns?.length > 0) {
            const customFields = displayColumns?.map(column => {
                const columnLength = column.title.length * 10;
                return {
                    title: column.title,
                    index: column.title,
                    width: (columnLength > 100 ? columnLength : 100) + 'px',
                };
            });
            columns.splice(insertLocation, 0, ...customFields);
        }
        return columns;
    }

    async getResourceFieldsAndColumns(originColumns: STColumn[], resourceKey: string, insertLocation: number = originColumns?.length - 1): Promise<ResourcesFieldColumn> {
        let columnsArr = [...originColumns];
        let fields: FieldParams[] = [];
        try {
            fields = await this.getCustomField(resourceKey) ?? [];
        } catch (e) {
            console.error('[getResourceFieldsAndColumns] Failed to fetch custom fields', {
                resourceKey,
                action: 'getCustomField',
                error: e,
            });
            fields = [];
        }
        if (fields.length > 0) {
            columnsArr = this.getResourceColumns(fields, columnsArr, insertLocation);
        }
        return {
            fields,
            columns: columnsArr,
        };
    }

    formatResourceList(arr: Chamber[] | Radio[] | UE[] | Resource[] | Matrix[] | BaseStation[], customFields: FieldParams[]): any {
        let data: any = arr;
        if (customFields?.length > 0 && arr && arr.length > 0) {
            data = arr?.map(item => {
                const fields = item.resourceInstance && item.resourceInstance?.fields ? item.resourceInstance?.fields : [];
                const fieldsValue: { [key: string]: string | number } = {};
                fields?.forEach(fieldItem => {
                    const key = `${fieldItem.resourceDefinitionFieldTitle}`;
                    fieldsValue[key] = fieldItem.value;
                });
                return {
                    ...item,
                    ...fieldsValue,
                };
            });
        }
        return data;
    }

    getResourceFieldInstance(radioFields: FieldParams[], data: { [key: string]: string | number }): ResourceInstancesField[] {
        let fields: any = [];
        if (radioFields?.length > 0) {
            fields = radioFields.map(item => {
                const { index, type, title, id } = item;
                return {
                    index,
                    type,
                    value: data[title] || '',
                    resourceDefinitionFieldId: id as number,
                    resourceDefinitionFieldTitle: title,
                };
            });
        }
        return fields;
    }

    getViewCanEdit(permission: string[]): boolean {
        return this.settingService.hasPermission(permission);
    }

    isHasChangeLabel(origin: number[], current: number[]): boolean {
        if (origin.length !== current.length) {
            return true;
        } else {
            return origin.some(item => current.indexOf(item) === -1);
        }
    }

    sendCommonEvent(name: string, value: boolean): void {
        this.commonEvent.emit({name, value});
    }

    watchCommonEvent(name: string): any {
        return this.commonEvent.pipe(filter((item) => item.name === name), map((item) => item.value));
    }

    EditIsHasChanged(currentData: EditForm, originData: EditForm, locationHasChanged: boolean = false, labelHasChanged: boolean = false): void {
        const formHasChanged = Object.keys(originData)?.some(key => {
            return currentData[key]?.toString() !== originData[key]?.toString();
        });
        if (formHasChanged || locationHasChanged || labelHasChanged) {
           this.modal.confirm({
                nzTitle: 'Do you want to Save the change(s)?',
                nzOkText: 'Yes',
                nzCancelText: 'No',
                nzOkType: 'primary',
                nzOkDanger: true,
                nzStyle: { top: '40vh' },
                nzOnOk: async () => {
                    console.log('click-ok');
                    this.sendCommonEvent('isSave', true);
                },
                nzOnCancel: () => {
                    console.log('click-cancel');
                    this.sendCommonEvent('isSave', false);
                }
            });
        } else {
            console.log('no-changes');
            this.sendCommonEvent('isSave', false);
        }
    }

    async getPortList(option: { resourceInstanceId: number, page: number, size: number }): Promise<ResourcePortData> {
        return lastValueFrom(this.http.get<ResourcePortData>(`${this.moduleApi}/resource-port`, {
            params: Object.assign(option),
        }));
    }

    exportResources(data: string, timeZone: string): void {
        const url = `${this.baseUrl}/export?${data}&timeZone=${timeZone}`;
        window.open(url);
    }

    async import(data: FormData, type?: string, sheetArr: string[] = []): Promise<any> {
        const originUrl = `${this.baseUrl}/import`;
        const sheets = sheetArr?.join('&sheetNames=');
        const url = type ? `${originUrl}?type=${type}&sheetNames=${sheets}` : `${originUrl}?sheetNames=${sheets}`;
        return lastValueFrom(this.http.post<void>(`${url}`, data));
    }

    async importCheck(sheetArr: string[], data: FormData): Promise<any> {
        const sheets = sheetArr?.join('&sheetNames=');
        return lastValueFrom(this.http.post<void>(`${this.baseUrl}/import-check?sheetNames=${sheets}`, data));
    }

    async getImportSheet(data: FormData): Promise<string[]> {
        return lastValueFrom(this.http.post<string[]>(`${this.baseUrl}/sheet-name`, data));
    }

    async portDelete(option: {id: number}): Promise<void> {
        return lastValueFrom(this.http.request<void>('DELETE', `${this.moduleApi}/resource-port`, {
            params: Object.assign(option),
        }));
    }

    async portAdd(option: ResourcePortAdd): Promise<ResourcePortList> {
        return lastValueFrom(this.http.post<ResourcePortList>(`${this.moduleApi}/resource-port`, option));
    }

    async portEdit(option: ResourcePortEdit): Promise<ChamberPort> {
        return lastValueFrom(this.http.put<ChamberPort>(`${this.moduleApi}/resource-port`, option));
    }

    getOtherCanEditPortResource(): string[] {
        // chamber, radio also can edit port name
        return ['PHYSICAL_SERVER', 'SWITCH_ROUTER', 'BBU', 'RF_SWITCH_SPLITTER', 'SHIELD_CHAMBER_BOX'];
    }

    getOtherResourceDefaultField(field: ResourceField[]): ResourceField[] {
        const otherField = [
            {
                label: 'Location',
                key: 'location',
                type: 'location',
            },
            {
                label: 'Tag',
                key: 'label',
                type: 'tag',
            },
            {
                label: 'Description',
                key: 'description',
                type: 'textarea',
            },
        ];
        const defaultFields: ResourceField[] = [
            {
                label: 'Name',
                key: 'name',
            }, ...field, ...otherField];
        return defaultFields.map((item) => {
            return {
                ...item,
                type: item?.type ? item.type : 'input',
            };
        });
    }


    // 返回值---originColumns: 最原始的列, fields: 自定义添加的字段数组, columns: 最后形成的字段数组
    async getResourceAllColumns(otherResourceColumns: STColumn[], resourceKey: string = 'CHAMBER', insertLocation?: number): Promise<ResourcesColumnData> {
        let originColumns: STColumn[] = [];
        if (resourceKey === 'CHAMBER') {
            originColumns = [
                {
                    title: 'Chamber',
                    index: 'name',
                    width: '120px',
                    sort: true,
                    sortOrder: 'ascend',
                    highlight: true,
                    isAutoWidth: true,
                    minWidth: 100
                },
                {
                    title: 'No. of Ports',
                    index: 'numOfPorts',
                    width: '130px',
                    highlight: true,
                    format: (data: STData) => {
                        return data.property.numOfPorts;
                    }
                },
                {
                    title: 'Location',
                    index: 'locationVO',
                    width: '200px',
                    format: (data: STData) => {
                        const location = data.locationVO;
                        return location?.parentNames ? (location?.parentNames.replace(/,/g, '/') + '/' + location?.name) : (location?.name ? location?.name : '');
                    },
                    highlight: true
                },
                {
                    title: 'Action',
                    className: 'remove-padding',
                    render: 'action',
                    width: '380px',
                    nzRight: true,
                },
            ];
        } else {
            const tableColumns = otherResourceColumns.map((item) => {
                return {
                    ...item,
                    highlight: true,
                    width: item.width || this.getColumnItemDefaultWidth(item.title || ''),
                };
            });
            originColumns = [
                {
                    title: 'Name',
                    index: 'name',
                    width: '120px',
                    highlight: true,
                },
                {
                    title: 'No. of Ports',
                    index: 'resourceInstance.port',
                    width: '130px',
                },
                ...tableColumns,
                {
                    title: 'Location',
                    index: 'locationVO',
                    width: '200px',
                    format: (data: STData) => {
                        const location = data.locationVO;
                        return location?.parentNames ? (location?.parentNames.replace(/,/g, '/') + '/' + location?.name) : (location?.name ? location?.name : '');
                    },
                    highlight: true,
                },
                {
                    title: 'Description',
                    index: 'description',
                    width: '130px',
                    highlight: true,
                },
                {
                    title: 'Created Date',
                    index: 'createdDate',
                    type: 'date',
                    dateFormat: 'YYYY-MM-dd HH:mm:ss',
                    width: '140px',
                    highlight: true,
                },
                {
                    title: 'Action',
                    className: 'remove-padding',
                    render: 'action',
                    width: '380px',
                    nzRight: true,
                },
            ];
        }
        const { fields, columns } = await this.getResourceFieldsAndColumns(originColumns, resourceKey, insertLocation);
        return {
            originColumns,
            fields,
            columns,
        };
    }

    getColumnItemDefaultWidth(title: string): string {
        return `${Math.max(10, title.length) * 10}px`;
    }

    async getGNBData(): Promise<GNBData[]> {
        return lastValueFrom(this.http.get<GNBData[]>(`${this.moduleApi}/gnb-cell/all`));
    }
}
